import { createI18n } from 'vue-i18n'

import en from '../locales/en.json'
import type { TranslationKey } from './i18nKeys'

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: 'en',
  fallbackLocale: 'en',
  messages: {
    en,
  },
})

/** vue-i18n's `t`, re-typed so this module can forward every call shape. */
const translate = i18n.global.t as unknown as (
  key: string,
  ...args: unknown[]
) => string

/**
 * A translation call whose key is guaranteed to exist in `en.json`.
 *
 * vue-i18n types its own `t()` key parameter as `string`, so an unknown key
 * compiles and only fails at runtime by rendering the raw key. This signature
 * narrows the key to the generated {@link TranslationKey} union, turning a typo
 * or a removed key into a compile error.
 *
 * Import `t` from this module (not `useI18n`) so the check actually applies.
 * The remaining overloads mirror the vue-i18n call shapes that interpolate
 * values, select a plural form, or supply a default message.
 */
export type TranslateFunction = {
  (key: TranslationKey): string
  (key: TranslationKey, named: Record<string, unknown>): string
  (key: TranslationKey, defaultMessage: string): string
  (key: TranslationKey, named: Record<string, unknown>, plural: number): string
  (
    key: TranslationKey,
    named: Record<string, unknown>,
    defaultMessage: string,
  ): string
}

export const t: TranslateFunction = (key: TranslationKey, ...args: unknown[]) =>
  translate(key, ...args)

/**
 * Alias of {@link t} kept for non-component call sites (stores, utilities) that
 * read better with a verb-like name.
 */
export const trans: TranslateFunction = t
